import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { SignUpElement } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { ConnectButton } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/ConnectButton";
import { LandingPageContext } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/LandingPageContext";
import { SECTION_PADDING_BREAKPOINT } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/Section";
import { Exclamation } from "@faire/web--source/slate/icons/foundation/Exclamation";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDetectScroll } from "@faire/web--source/ui/hooks/useDetectScroll";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useContext, useEffect, useState } from "react";
import React from "react";
import styled, { css } from "styled-components";
export var StickyCTABanner = function() {
    var isMobile = useViewport().isMobile;
    var isRetailerSubUser = useUserStore([
        "isRetailerSubUser"
    ]).isRetailerSubUser;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var isLinked = usePOSSummary().isLinked;
    var clickEvent = useContext(LandingPageContext).clickEvent;
    var _useState = _sliced_to_array(useState(false), 2), showBanner = _useState[0], setShowBanner = _useState[1];
    var scrollPosition = useDetectScroll().scrollPosition;
    var headerPlusPromoBannerHeight = useTopOfPageContent().headerPlusPromoBannerHeight;
    var scrollPositionThreshold = headerPlusPromoBannerHeight + (isMobile ? 234 : 764);
    useEffect(function() {
        if (!showBanner && scrollPosition > scrollPositionThreshold) {
            setShowBanner(true);
        } else if (showBanner && scrollPosition < scrollPositionThreshold) {
            setShowBanner(false);
        }
    }, [
        showBanner,
        scrollPosition,
        scrollPositionThreshold
    ]);
    // Hide the sticky CTA if the logged-in user is a sub user and the POS is not yet linked
    var hideStickyCTA = isLoggedInRetailer && isRetailerSubUser && !isLinked;
    if (isMobile) {
        return hideStickyCTA ? null : /*#__PURE__*/ _jsx(MobileContainer, {
            $showBanner: showBanner,
            children: /*#__PURE__*/ _jsx(ConnectButton, {
                onClick: function() {
                    return clickEvent === null || clickEvent === void 0 ? void 0 : clickEvent("sticky");
                },
                element: SignUpElement.NUDGE
            })
        });
    }
    return hideStickyCTA ? /*#__PURE__*/ _jsxs(SubUserBanner, {
        gap: "1x",
        children: [
            /*#__PURE__*/ _jsx(Exclamation, {}),
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "hBa/L5",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "You must be an account owner to connect your POS to Faire."
                        }
                    ]
                })
            })
        ]
    }) : /*#__PURE__*/ _jsx(DesktopContainer, {
        $showBanner: showBanner,
        children: /*#__PURE__*/ _jsxs(DesktopContent, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "subheadingSansMedium",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "ie/8jr",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Say goodbye to manual data entry"
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(ConnectButton, {
                    onClick: function() {
                        return clickEvent === null || clickEvent === void 0 ? void 0 : clickEvent("sticky");
                    },
                    element: SignUpElement.NUDGE,
                    small: true
                })
            ]
        })
    });
};
var TRANSITION_DURATION_MS = 300;
var SubUserBanner = styled(Row).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-31249e90-0"
})([
    "width:100%;height:",
    ";background-color:",
    ";"
], getSpacing("6x"), Core.surface.warning);
var MobileContainer = styled.div.withConfig({
    componentId: "sc-31249e90-1"
})([
    "",
    ";transition:transform ",
    "ms ease-in-out,visibility 0ms ",
    ";position:fixed;bottom:0;z-index:11;width:100%;background-color:",
    ";border-top:1px solid ",
    ";box-shadow:0px -4px 6px 0px #00000026;padding:",
    ";"
], function(param) {
    var $showBanner = param.$showBanner;
    if ($showBanner) {
        return css([
            "transform:translateY(0);visibility:visible;"
        ]);
    } else {
        return css([
            "transform:translateY(100%);visibility:hidden;"
        ]);
    }
}, TRANSITION_DURATION_MS, function(param) {
    var $showBanner = param.$showBanner;
    return $showBanner ? "0ms" : css([
        "",
        "ms"
    ], TRANSITION_DURATION_MS);
}, Core.surface.inverse, Core.border.muted, getSpacing("2x", "2x", "3x", "2x"));
var DesktopContainer = styled.div.withConfig({
    componentId: "sc-31249e90-2"
})([
    "",
    ";transition:transform ",
    "ms ease-in-out,visibility 0ms ",
    ";position:fixed;top:0;z-index:11;width:100%;background-color:",
    ";border-bottom:1px solid ",
    ";box-shadow:0px 4px 6px 0px #00000026;padding:",
    ";@media (min-width:",
    "px){padding:0;}"
], function(param) {
    var $showBanner = param.$showBanner;
    if ($showBanner) {
        return css([
            "transform:translateY(0);visibility:visible;"
        ]);
    } else {
        return css([
            "transform:translateY(-100%);visibility:hidden;"
        ]);
    }
}, TRANSITION_DURATION_MS, function(param) {
    var $showBanner = param.$showBanner;
    return $showBanner ? "0ms" : css([
        "",
        "ms"
    ], TRANSITION_DURATION_MS);
}, Core.surface.inverse, Core.border.muted, getSpacing(0, "3x"), SECTION_PADDING_BREAKPOINT);
var DesktopContent = styled(Row).attrs({
    align: "center",
    justify: "space-between"
}).withConfig({
    componentId: "sc-31249e90-3"
})([
    "margin:0 auto;width:100%;max-width:1168px;padding:",
    ";"
], getSpacing("2x", 0));
